/**
 * Bekleyen cevap kuyruğu (8 Eki 2026).
 *
 * Eskiden oynatıcı her cevabı gönderip sunucunun yanıtını bekliyor, bu sırada ileri düğmesini kilitliyordu.
 * Okul ağında bağlantı gidip gelince çocuk dakikalarca aynı soruda kalıyordu. Artık anaokulu oturum sayfasındaki
 * gibi: cevap kuyruğa yazılır, çocuk hemen ilerler, kuyruk arka planda sırayla gönderilir ve bağlantı gelince
 * kendiliğinden tamamlanır.
 *
 * - Sıra: en küçük soru sırası önce gider. Sunucu yalnız ilk cevapsız soruyu ve bir öncekini kabul ettiği için
 *   (RunnerAnswerNavigation) sırayla göndermek şart.
 * - Kalıcılık: kuyruk localStorage'da tutulur; sayfa kapanıp yeniden açılsa da bekleyen cevaplar gönderilir.
 *   attemptId aynı kaldığı için tekrar gönderim çift kayıt oluşturmaz.
 * - Sunucu cevabı kalıcı olarak reddederse (ör. invalid_navigation: sunucuda kayıtlı cevap geçerli kalır)
 *   kayıt kuyruktan düşer; ağ/sunucu hatasında artan aralıklarla süresiz yeniden denenir.
 */
import { ApiError } from "../api/client";
import type { MechanicAnswer } from "../mechanics/types";

export interface QueuedBody {
  attemptId: string;
  answer: MechanicAnswer;
  durationMs: number;
}

export interface QueuedAnswer {
  itemId: number;
  /** Oturumdaki soru sırası (gönderim sırası buna göre). */
  ix: number;
  body: QueuedBody;
  /** Kuyruğa giriş zamanı (ms). */
  at: number;
}

export interface QueueState {
  pending: number;
  /** En eski bekleyen cevabın kuyruğa giriş zamanı; kuyruk boşsa null. */
  oldestAt: number | null;
}

/** Ağ/sunucu hatasından sonra yeniden deneme aralıkları (sonuncusu tekrarlanır). */
export const KUYRUK_BEKLEME_MS = [2000, 4000, 8000, 15000, 30000];

const KEY_PREFIX = "ecup.runner.bekleyen.";

function storageKey(sessionCode: string): string {
  return KEY_PREFIX + sessionCode;
}

/** localStorage'daki bekleyen cevaplar (okunamazsa boş). */
export function loadQueued(sessionCode: string): QueuedAnswer[] {
  try {
    const raw = window.localStorage.getItem(storageKey(sessionCode));
    const list = raw ? (JSON.parse(raw) as QueuedAnswer[]) : [];
    return Array.isArray(list) ? list.filter((x) => x && typeof x.itemId === "number" && x.body) : [];
  } catch {
    return [];
  }
}

export function clearQueued(sessionCode: string): void {
  try {
    window.localStorage.removeItem(storageKey(sessionCode));
  } catch {
    /* depolama kapalı olabilir */
  }
}

/** Yeniden denemenin işe yaramayacağı ret: sunucuda kayıtlı durum geçerli kalır, cevap kuyruktan düşer. */
export function isPermanentRejection(e: unknown): boolean {
  if (e instanceof ApiError) {
    return e.status === 400 || e.status === 404 || e.status === 409 || e.status === 422;
  }
  return typeof e === "object" && e !== null && (e as { code?: unknown }).code === "invalid_navigation";
}

export class AnswerQueue {
  private entries = new Map<number, QueuedAnswer>();
  private running = false;
  private disposed = false;
  private waiters: Array<() => void> = [];
  private wakeUp: (() => void) | null = null;

  constructor(
    private readonly sessionCode: string,
    private readonly send: (itemId: number, body: QueuedBody) => Promise<void>,
    private readonly onChange: (s: QueueState) => void = () => {},
    private readonly delays: number[] = KUYRUK_BEKLEME_MS,
  ) {
    for (const q of loadQueued(sessionCode)) this.entries.set(q.itemId, q);
  }

  get state(): QueueState {
    let oldestAt: number | null = null;
    for (const q of this.entries.values()) if (oldestAt === null || q.at < oldestAt) oldestAt = q.at;
    return { pending: this.entries.size, oldestAt };
  }

  /** Cevabı kuyruğa yazar (aynı sorunun eski bekleyen cevabının yerine geçer) ve gönderimi başlatır. */
  enqueue(itemId: number, ix: number, body: QueuedBody): void {
    if (this.disposed) return;
    this.entries.set(itemId, { itemId, ix, body, at: Date.now() });
    this.changed();
    // Yeni cevap geldiyse bekleme aralığını beklemeden hemen yeniden dene.
    this.retryNow();
  }

  /** Bekleyen gönderim varsa beklemeyi keser, hemen yeniden dener (ör. "Tekrar dene"). */
  retryNow(): void {
    this.wakeUp?.();
    this.kick();
  }

  /** Kuyruk boşalınca true; süre dolarsa false. */
  drain(timeoutMs: number): Promise<boolean> {
    if (this.entries.size === 0) return Promise.resolve(true);
    this.retryNow();
    return new Promise((resolve) => {
      let bitti = false;
      const t = setTimeout(() => {
        if (bitti) return;
        bitti = true;
        this.waiters = this.waiters.filter((w) => w !== ok);
        resolve(false);
      }, timeoutMs);
      const ok = () => {
        if (bitti) return;
        bitti = true;
        clearTimeout(t);
        resolve(true);
      };
      this.waiters.push(ok);
    });
  }

  dispose(): void {
    this.disposed = true;
    this.wakeUp?.();
  }

  /** Oturum bitti: saklanan kuyruk silinir. */
  clear(): void {
    this.entries.clear();
    clearQueued(this.sessionCode);
    this.changed();
  }

  kick(): void {
    if (this.running || this.disposed) return;
    this.running = true;
    void this.loop();
  }

  private async loop(): Promise<void> {
    let hata = 0;
    try {
      while (!this.disposed && this.entries.size > 0) {
        const next = [...this.entries.values()].sort((a, b) => a.ix - b.ix || a.at - b.at)[0]!;
        try {
          await this.send(next.itemId, next.body);
          this.removeIfSame(next);
          hata = 0;
        } catch (e) {
          if (isPermanentRejection(e)) {
            this.removeIfSame(next);
            continue;
          }
          const bekle = this.delays[Math.min(hata, this.delays.length - 1)]!;
          hata++;
          await new Promise<void>((r) => {
            const t = setTimeout(() => { this.wakeUp = null; r(); }, bekle);
            this.wakeUp = () => { clearTimeout(t); this.wakeUp = null; r(); };
          });
        }
      }
    } finally {
      this.running = false;
    }
    if (this.entries.size === 0) {
      const w = this.waiters;
      this.waiters = [];
      w.forEach((f) => f());
    }
  }

  /** Gönderim sürerken aynı soru yeniden cevaplandıysa yeni cevap kuyrukta kalır. */
  private removeIfSame(sent: QueuedAnswer): void {
    const cur = this.entries.get(sent.itemId);
    if (cur && cur.body.attemptId === sent.body.attemptId) {
      this.entries.delete(sent.itemId);
      this.changed();
    }
  }

  private changed(): void {
    try {
      if (this.entries.size === 0) window.localStorage.removeItem(storageKey(this.sessionCode));
      else window.localStorage.setItem(storageKey(this.sessionCode), JSON.stringify([...this.entries.values()]));
    } catch {
      /* depolama kapalı/dolu: kuyruk bellekte çalışmaya devam eder */
    }
    this.onChange(this.state);
  }
}
